import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { apiGetAvailableCoupons } from '../../services/CouponService';
import {
  isSpendGetOffCoupon,
  isSpendGetFreeItemCoupon,
  isItemWithCouponBenefits,
} from '../coupon';

/**
 * Fetches the applied coupon and calculates whether the minimum price
 * requirement is met. Returns `null` when no warning is needed.
 *
 * Returns:
 *   { needed: number, discountText: string, minprice: number, type: string }
 *   or null
 */
const useCouponMinPriceInfo = () => {
  const basket = useSelector((state) => state.basket.basket);
  const currentStore = useSelector((state) => state.base.common.currentStore);
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  // Fetch the full coupon object when discount code changes
  useEffect(() => {
    if (!basket.discountcode || !currentStore?.id) {
      setAppliedCoupon(null);
      return;
    }

    let cancelled = false;
    const fetchCoupon = async () => {
      try {
        const response = await apiGetAvailableCoupons(currentStore.id, 10);
        if (cancelled) return;

        let couponData = null;
        if (Array.isArray(response)) couponData = response;
        else if (Array.isArray(response?.data?.data)) couponData = response.data.data;
        else if (Array.isArray(response?.data)) couponData = response.data;

        setAppliedCoupon(
          Array.isArray(couponData)
            ? (couponData.find((c) => c.code === basket.discountcode) ?? null)
            : null,
        );
      } catch {
        if (!cancelled) setAppliedCoupon(null);
      }
    };

    fetchCoupon();
    return () => { cancelled = true; };
  }, [basket.discountcode, currentStore?.id]);

  const couponMinPriceInfo = useMemo(() => {
    if (!appliedCoupon) return null;

    // ── Type 0: Free Item ──────────────────────────────────────────
    if (isSpendGetFreeItemCoupon(appliedCoupon)) {
      const regularItems = basket.items.filter(
        (item) =>
          !item.isFreeItemFromCoupon &&
          !isItemWithCouponBenefits(item),
      );
      let cleanSubtotal = regularItems.reduce((sum, item) => sum + (item.total || 0), 0);

      const freeItem = basket.items.find(
        (item) =>
          appliedCoupon?.freeitemid === item.item?.id &&
          item.isFreeItemFromCoupon === true,
      );

      if (freeItem && freeItem.qty > 1) {
        const originalItem = freeItem.product?.items?.find((p) => p.id === freeItem.item?.id);
        const itemPrice = originalItem?.price || freeItem.item?.price || 0;
        const optionsPricePerItem = (freeItem.options || []).reduce(
          (sum, opt) => sum + (opt.price || 0) * (opt.qty ?? 1),
          0,
        );
        cleanSubtotal += (itemPrice + optionsPricePerItem) * (freeItem.qty - 1);
      }

      if (appliedCoupon.minprice && cleanSubtotal < appliedCoupon.minprice) {
        return {
          needed: appliedCoupon.minprice - cleanSubtotal,
          discountText: 'Free item',
          minprice: appliedCoupon.minprice,
          type: 'freeItem',
        };
      }
      return null;
    }

    // ── Type 1: Spend Get Off ──────────────────────────────────────
    if (isSpendGetOffCoupon(appliedCoupon)) {
      const regularItems = basket.items.filter((item) => !isItemWithCouponBenefits(item));
      const cleanSubtotal = regularItems.reduce((sum, item) => sum + (item.total || 0), 0);

      if (appliedCoupon.minprice && cleanSubtotal < appliedCoupon.minprice) {
        const isPercentage = appliedCoupon.discounttype === 0;
        return {
          needed: appliedCoupon.minprice - cleanSubtotal,
          discountText: isPercentage
            ? `${appliedCoupon.discountamount}% off`
            : `$${appliedCoupon.discountamount} off`,
          minprice: appliedCoupon.minprice,
          type: 'discount',
        };
      }
      return null;
    }

    return null;
  }, [appliedCoupon, basket.items]);

  return couponMinPriceInfo;
};

export default useCouponMinPriceInfo;
